// src/context/NotificationContext.js
import { createContext, useState, useEffect, useContext, useRef, useCallback } from "react";
import { AuthContext } from "./AuthContext";
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from "../api/notificationsApi";

export const NotificationContext = createContext();

const POLL_INTERVAL_MS = 30_000; // 30 seconds

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated } = useContext(AuthContext);
  const [notifications, setNotifications]   = useState([]);
  const [unreadCount,   setUnreadCount]     = useState(0);
  const [loading,       setLoading]         = useState(false);
  const intervalRef = useRef(null);

  // ── Fetch from API ─────────────────────────────────────
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await getNotifications();
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count    || 0);
    } catch (err) {
      // Silently fail — don't spam the user with error toasts for a poll
      console.warn("[notifications] Poll failed:", err?.message);
    }
  }, [isAuthenticated]);

  // ── Start / stop polling based on auth state ───────────
  useEffect(() => {
    if (isAuthenticated) {
      setLoading(true);
      fetchNotifications().finally(() => setLoading(false));

      // Poll every 30 s
      intervalRef.current = setInterval(fetchNotifications, POLL_INTERVAL_MS);
    } else {
      // Clear everything on logout
      setNotifications([]);
      setUnreadCount(0);
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [isAuthenticated, fetchNotifications]);

  // ── Mark one as read ───────────────────────────────────
  const markRead = async (id) => {
    // Optimistic update
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));

    try {
      await markNotificationRead(id);
    } catch (err) {
      console.warn("[notifications] markRead failed:", err?.message);
      fetchNotifications(); // revert via fresh data
    }
  };

  // ── Mark all as read ──────────────────────────────────
  const markAllRead = async () => {
    // Optimistic update
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);

    try {
      await markAllNotificationsRead();
    } catch (err) {
      console.warn("[notifications] markAllRead failed:", err?.message);
      fetchNotifications();
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        markRead,
        markAllRead,
        refresh: fetchNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
